import { SgSelectOption } from '../../models/select-option.model';

/**
 * Date-range presets for `sg-date-range`. Every preset except `custom` is a rolling
 * window anchored to "now" with NO upper bound (`toDate` stays open, so newly-created
 * rows keep showing up without the user re-picking a filter); `custom` exposes an
 * explicit from/to Material range picker instead.
 */
export type DatePreset = 'today' | 'pastHour' | 'past24h' | 'past7days' | 'past30days' | 'allTime' | 'custom';

/**
 * The ControlValueAccessor value of `sg-date-range` — the user's *selection state*,
 * NOT a frozen `{from,to}` range. Rolling presets carry only `preset`; `custom` (and
 * the no-preset mode) additionally carry calendar-date-only strings (`YYYY-MM-DD`).
 * Callers resolve this to concrete query bounds AT QUERY TIME via `resolveDateRange`,
 * so a preset like "past hour" always stays relative to the actual fetch.
 */
export type DateRangeValue = { preset?: DatePreset; fromDate?: string; toDate?: string };

/** Options for the preset dropdown, in display order. */
export const DATE_PRESET_OPTIONS: SgSelectOption<DatePreset>[] = [
  { value: 'today', label: 'Today' },
  { value: 'pastHour', label: 'Past hour' },
  { value: 'past24h', label: 'Past 24 hours' },
  { value: 'past7days', label: 'Past 7 days' },
  { value: 'past30days', label: 'Past 30 days' },
  { value: 'allTime', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

/**
 * Resolves a `DateRangeValue` selection to concrete `fromDate`/`toDate` ISO strings.
 *
 * Copied VERBATIM from conversations-list's former `computeDateRange()` — deliberately
 * built from *local* `Date` math (not UTC): every preset besides `today`/`custom` is a
 * rolling "from N-ago to now" window (no `toDate`, so newly-arrived rows keep showing);
 * `today` anchors `fromDate` to local midnight; `allTime` sends neither bound; `custom`
 * (and the no-preset case, i.e. `preset` undefined) converts the picked `YYYY-MM-DD`
 * values to local start-of-day / end-of-day ISO strings.
 *
 * MUST be called at QUERY TIME inside the caller's fetch method (as conversations-list
 * calls it inside `fetchPage()`), not frozen into the FormControl at selection — this
 * keeps rolling windows relative to the fetch and preserves pagination re-anchoring.
 */
export function resolveDateRange(value: DateRangeValue | null | undefined): { fromDate?: string; toDate?: string } {
  const preset = value?.preset;
  const now = new Date();

  switch (preset) {
    case 'today':
      return { fromDate: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString() };
    case 'pastHour':
      return { fromDate: new Date(now.getTime() - 60 * 60 * 1000).toISOString() };
    case 'past24h':
      return { fromDate: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString() };
    case 'past7days':
      return { fromDate: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString() };
    case 'past30days':
      return { fromDate: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString() };
    case 'allTime':
      return {};
    case 'custom':
    default:
      // `custom` AND no-preset mode both normalize the picked from/to to local
      // start-of-day / end-of-day, exactly as the old native <input type="date"> path did.
      return {
        fromDate: value?.fromDate ? new Date(`${value.fromDate}T00:00:00`).toISOString() : undefined,
        toDate: value?.toDate ? new Date(`${value.toDate}T23:59:59.999`).toISOString() : undefined,
      };
  }
}
